<template>
  <div
    v-if="hasSidebarContent"
    class="top-navigation-desktop h-min xl:relative"
  >
    <div class="grid grid-cols-1 sm:grid-cols-2 md:gap-y-7 xl:grid-cols-1">
      <!-- COMPANY DESKTOP -->
      <ul
        class="bg-gradient-to-tr from-[#F7FBFE] to-[#EFF6FA] py-8 max-sm:hidden sm:px-8 sm:py-16 xl:pb-10 xl:pt-7"
      >
        <h2 class="mb-5 text-md font-medium sm:mb-7">
          {{ $t('facts_and_figures') }} {{ $t('enterprise') }}
        </h2>
        <ul class="grid gap-5 sm:gap-7">
          <li v-for="item in contentCompany" :key="item.i18nLabel">
            <SustainabilityFactsSidebarItem :data="item" />
          </li>
        </ul>
      </ul>

      <!-- COMPANY MOBILE -->
      <div class="sm:hidden">
        <DetailSummary
          class="bg-gradient-to-tr from-[#F7FBFE] to-[#EFF6FA] px-container-wide"
          :duration-open="400"
        >
          <template #summary>
            <div
              class="max-w-title py-4 text-md font-medium sm:pr-10 sm:text-lg"
            >
              {{ $t('facts_and_figures') }} {{ $t('enterprise') }}
            </div>
          </template>
          <template #content>
            <ul class="grid gap-5 pb-8 pt-5 sm:gap-7">
              <li v-for="item in contentCompany" :key="item.i18nLabel">
                <SustainabilityFactsSidebarItem :data="item" />
              </li>
            </ul>
          </template>
        </DetailSummary>
      </div>

      <!-- PROJECT DESKTOP -->
      <ul
        class="bg-gradient-to-tr from-[#F7FBFE] to-[#EFF6FA] px-container-wide py-8 max-sm:hidden sm:px-8 sm:py-16 xl:pb-10 xl:pt-7"
      >
        <h2 class="mb-5 text-md font-medium sm:mb-7">
          {{ $t('facts_and_figures') }} {{ $t('project') }}
        </h2>
        <ul class="grid gap-5 sm:gap-7">
          <li v-for="item in contentProject" :key="item.i18nLabel">
            <SustainabilityFactsSidebarItem :data="item" />
          </li>
        </ul>
      </ul>

      <!-- PROJECT MOBILE -->
      <div class="sm:hidden">
        <DetailSummary
          class="bg-gradient-to-tr from-[#F7FBFE] to-[#EFF6FA] px-container-wide"
          :duration-open="400"
        >
          <template #summary>
            <div
              class="max-w-title py-4 text-md font-medium sm:pr-10 sm:text-lg"
            >
              {{ $t('facts_and_figures') }} {{ $t('project') }}
            </div>
          </template>
          <template #content>
            <ul class="grid gap-5 pb-8 pt-5 sm:gap-7">
              <li v-for="item in contentProject" :key="item.i18nLabel">
                <SustainabilityFactsSidebarItem :data="item" />
              </li>
            </ul>
          </template>
        </DetailSummary>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useAuthStore } from '~/stores/auth.js'

const props = defineProps({
  page: { type: Object, required: true },
})

const contentCompany = computed(() => {
  // [propName, i18nLabel, icon, i18nValueSuffix, linkGetter, valueGetter]
  const items = [
    [
      'link_text_company',
      'company',
      'CompanyIcon',
      undefined,
      (page) => page.link_company,
    ],
    [
      'operating_time',
      'operating_time',
      'SunColdIcon',
      undefined,
      undefined,
      (v) => v?.title,
    ],
    ['amount_employees', 'amount_employees', 'EmployeesIcon'],
    [
      'passenger_transport_revenue',
      'passenger_transport_revenue',
      'PassengerTransportIcon',
    ],
    [
      'height_of_mountain_station',
      'height_of_mountain_station',
      'MountainIcon',
      'above_sea_level',
    ],
    ['km_of_slopes', 'km_of_slopes', 'RulerIcon', 'km'],
  ]

  return mapContentToSidebarItem(items)
})

const contentProject = computed(() => {
  // [propName, i18nLabel, icon, i18nValueSuffix, linkGetter]
  const items = [
    ['project_duration', 'project_duration', 'ProjectTimeIcon'],
    ['costs', 'project_costs', 'MoneyBagIcon'],
    ['benefit', 'project_benefit', 'BenefitIcon'],
    ['partners', 'partners', 'shakingHandsIcon'],
    ['support', 'support', 'walletIcon'],
  ]

  return mapContentToSidebarItem(items)
})

function mapContentToSidebarItem(items) {
  return items
    .filter(([propName]) => props.page[propName])
    .map(
      ([
        propName,
        i18nLabel,
        icon,
        i18nValueSuffix,
        linkGetter,
        valueGetter,
      ]) => ({
        i18nLabel,
        icon,
        value: valueGetter
          ? valueGetter(props.page[propName])
          : props.page[propName],
        i18nValueSuffix,
        ...(linkGetter && { link: linkGetter(props.page) }),
      })
    )
}

const { isMember, isRegularMember } = useAuthStore()

const showContactPerson = computed(() => {
  const visibility = props.page.contact_visibility.value

  switch (visibility) {
    case 'all':
      return true
    case 'members':
      return isMember || isRegularMember
    case 'regular_members':
      return isRegularMember
  }
})

const hasSidebarContent = computed(() => {
  return (
    props.page.link_text_company ||
    props.page.project_duration ||
    props.page.contact
  )
})
</script>
